import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    NgZone,
    OnDestroy,
    inject,
    input,
    computed,
    output,
    signal,
} from '@angular/core'
import { CurrencyPipe } from '@angular/common'

import type { Venue } from '../../../domain/Venue'
import type { PaymentFormStatus } from '../../../utilities/form-status'
import type { PaymentIntent } from '../../../domain/PaymentIntent'
import type { PaymentSession, PaymentUsage } from '../../../services/PaymentSession'
import { PaymentSessionValidationError } from '../../../services/PaymentSession'
import { PaymentsService } from '../../../services/payments.service'
import { CurrencyUnitPipe } from '../../../pipes/currency-unit.pipe'

let nextPaymentElementId = 0

@Component({
    selector: 'app-venue-storefront-booking-payment-element',
    standalone: true,
    templateUrl: './venue-storefront-booking-payment-element.component.html',
    styleUrls: ['./venue-storefront-booking-payment-element.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CurrencyUnitPipe],
    providers: [CurrencyPipe],
})
export class VenueStorefrontBookingPaymentElementComponent implements AfterViewInit, OnDestroy {
    venue = input.required<Venue>()
    intent = input.required<PaymentIntent>()
    usage = input.required<PaymentUsage>()
    saveCardWithPayment = input(false)
    billingEmailAddress = input.required<string>()
    futureChargeAmount = input<number | null>(null)
    currencyCode = input<string | null>(null)
    readonly paymentAccount = computed(() => this.venue().paymentsAccountForCapability(
        this.usage() === 'setup-card' ? 'CancellationChargePayments' : 'DepositPayments'
    ))

    statusChange = output<PaymentFormStatus>()
    confirmed = output<string>()
    readyChange = output<boolean>()
    confirmingChange = output<boolean>()

    protected readonly errorMessage = signal<string | null>(null)
    protected readonly isReady = signal(false)
    protected readonly isConfirming = signal(false)
    protected readonly walletPaymentAvailable = signal(false)
    protected readonly usesHostedCheckout = signal(false)
    protected readonly requiresFutureChargeConsent = signal(false)
    protected readonly futureChargeConsentGiven = signal(false)
    protected readonly cardFormId = `venue-storefront-payment-card-${++nextPaymentElementId}`
    protected readonly applePayId = `${this.cardFormId}-apple-pay`
    protected readonly googlePayId = `${this.cardFormId}-google-pay`

    private readonly paymentsService = inject(PaymentsService)
    private readonly ngZone = inject(NgZone)
    private paymentSession: PaymentSession | null = null
    private destroyed = false

    ngAfterViewInit(): void {
        void this.mountPaymentSession()
    }

    ngOnDestroy(): void {
        this.destroyed = true
        this.paymentSession?.destroy()
        this.paymentSession = null
    }

    async confirm(): Promise<void> {
        if (!this.paymentSession || !this.isReady() || this.isConfirming()) {
            return
        }
        if (this.requiresFutureChargeConsent() && !this.futureChargeConsentGiven()) {
            this.errorMessage.set(
                'Please agree to the cancellation and no-show charge before continuing.'
            )
            return
        }

        this.setConfirming(true)
        this.errorMessage.set(null)
        try {
            const intentId = await this.paymentSession.confirm(this.billingEmailAddress())
            this.requireCurrentIntent(intentId)
            if (!this.destroyed) {
                this.confirmed.emit(intentId)
            }
        } catch (error) {
            if (!this.destroyed) {
                if (error instanceof PaymentSessionValidationError) {
                    this.statusChange.emit('validation_failed')
                } else {
                    this.showError(error, this.confirmationErrorMessage())
                }
            }
        } finally {
            if (!this.destroyed) {
                this.setConfirming(false)
            }
        }
    }

    private async mountPaymentSession(): Promise<void> {
        try {
            const session = await this.paymentsService.createPaymentSession(this.venue(), {
                intent: this.intent(),
                usage: this.usage(),
                saveCardWithPayment: this.saveCardWithPayment(),
                hasFutureChargeConsent: () => this.futureChargeConsentGiven(),
                cardFormSelector: `#${this.cardFormId}`,
                getBillingEmailAddress: () => this.billingEmailAddress(),
                applePayWalletButtonSelector: `#${this.applePayId}`,
                googlePayWalletButtonSelector: `#${this.googlePayId}`,
            })
            if (this.destroyed) {
                session.destroy()
                return
            }

            this.paymentSession = session
            session.setOnWalletConfirmed(intentId => {
                if (!this.destroyed) {
                    this.ngZone.run(() => {
                        try {
                            this.requireCurrentIntent(intentId)
                            this.confirmed.emit(intentId)
                        } catch (error) {
                            this.showError(error, this.confirmationErrorMessage())
                        }
                    })
                }
            })
            session.setOnError(message => {
                if (!this.destroyed) {
                    this.ngZone.run(() => {
                        // confirm() reports its own failures; callbacks also cover wallet errors.
                        if (!this.isConfirming()) {
                            this.statusChange.emit('confirmation_failed')
                        }
                        this.errorMessage.set(message || this.confirmationErrorMessage())
                    })
                }
            })
            this.walletPaymentAvailable.set(session.walletPaymentAvailable)
            this.usesHostedCheckout.set(session.usesHostedCheckout === true)
            this.requiresFutureChargeConsent.set(session.usesHostedCheckout === true || session.requiresFutureChargeConsent === true)
            this.isReady.set(true)
            this.readyChange.emit(true)
            this.statusChange.emit('ready')
        } catch {
            if (!this.destroyed) {
                this.showSetupError()
            }
        }
    }

    private showSetupError(): void {
        const message = 'There was an error setting up the payment form. Please refresh the page and try again.'
        this.statusChange.emit('setup_failed')
        this.errorMessage.set(message)
    }

    private showError(error: unknown, fallback: string): void {
        const message = error instanceof Error && error.message.length > 0
            ? error.message
            : fallback
        this.statusChange.emit('confirmation_failed')
        this.errorMessage.set(message)
    }

    private setConfirming(isConfirming: boolean): void {
        this.isConfirming.set(isConfirming)
        this.confirmingChange.emit(isConfirming)
    }

    private confirmationErrorMessage(): string {
        return this.usage() === 'payment'
            ? 'There was an error taking your payment. Please try again.'
            : 'There was an error saving your card details. Please try again.'
    }

    private requireCurrentIntent(intentId: string): void {
        if (intentId !== this.intent().id) {
            throw new Error('The confirmed payment did not match the current payment.')
        }
    }
}
